import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { DozentBot, TutorService } from '../../services/tutor.service';
import { OrganisationService } from '../../services/organisation.service';

/**
 * Reusable Dozent-link picker.
 *
 * Renders a "currently linked" card when a dozent is attached, and an
 * inline edit panel (nb-select + create button) when the user switches
 * to edit mode. Changes are confirmed via NbDialogService before they
 * hit the backend so a stray click can't silently re-link a tutor to a
 * different dozent.
 *
 * The currently linked dozent is rendered from the resolved
 * `DozentBot` object — independent of nb-select's option matching —
 * which guarantees the name shows up even when the dozents list and
 * the attached link arrive on different ticks.
 */
@Component({
  selector: 'app-dozent-link-picker',
  templateUrl: './dozent-link-picker.component.html',
  styleUrl: './dozent-link-picker.component.scss',
})
export class DozentLinkPickerComponent implements OnInit, OnChanges {
  @Input() tutorId!: string;
  @Input() disabled = false;

  @Output() linkChanged = new EventEmitter<string | null>();

  dozents: DozentBot[] = [];

  /** `''` means "no dozent". Always reflects the currently saved link. */
  private originalDozentId: string = '';

  /** Staged value while the user is picking a different dozent. */
  pendingDozentId: string = '';

  /** Toggles the edit panel; auto-on when no dozent is linked. */
  editMode = false;

  loading = false;

  createDialogVisible = false;
  createForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    public tutorService: TutorService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private translate: TranslateService,
    private organisationService: OrganisationService
  ) {
    this.createForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
    });
  }

  ngOnInit() {
    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tutorId'] && !changes['tutorId'].firstChange) {
      this.refresh();
    }
  }

  /** Resolved DozentBot for the currently saved link, used by the
   *  display card. Returns undefined while the list is still loading
   *  or when nothing is linked. */
  get currentDozent(): DozentBot | undefined {
    if (!this.originalDozentId) return undefined;
    return this.dozents.find((d) => d.id === this.originalDozentId);
  }

  get hasLink(): boolean {
    return !!this.originalDozentId;
  }

  /** True when the staged picker value differs from what's saved — only
   *  then is the "Bestätigen" button meaningful. */
  get hasPendingChange(): boolean {
    return this.pendingDozentId !== this.originalDozentId;
  }

  private refresh() {
    if (!this.tutorId) return;
    this.loading = true;
    // Resolve dozents list AND the attached link in lock-step so the
    // display card can render the linked dozent's name immediately.
    forkJoin({
      list: this.tutorService.listDozentBots(),
      attached: this.tutorService.getAttachedDozent(this.tutorId),
    }).subscribe({
      next: ({ list, attached }) => {
        this.dozents = list ?? [];
        this.originalDozentId = attached?.dozent_bot_id ?? '';
        this.pendingDozentId = this.originalDozentId;
        this.editMode = !this.originalDozentId; // auto-edit when empty
        this.loading = false;
      },
      error: () => {
        this.dozents = [];
        this.originalDozentId = '';
        this.pendingDozentId = '';
        this.editMode = true;
        this.loading = false;
      },
    });
  }

  enterEditMode() {
    this.pendingDozentId = this.originalDozentId;
    this.editMode = true;
  }

  cancelEdit() {
    this.pendingDozentId = this.originalDozentId;
    this.editMode = false;
    this.createDialogVisible = false;
  }

  onPickerChange(value: string) {
    this.pendingDozentId = value ?? '';
  }

  /** Open the "Ja/Nein" confirm dialog and apply the change on confirm. */
  confirmApplyChange() {
    if (!this.hasPendingChange) {
      this.editMode = false;
      return;
    }

    // Detach
    if (!this.pendingDozentId) {
      this.openConfirm(
        'tutor.dozentLink.confirmDetachTitle',
        'tutor.dozentLink.confirmDetachMessage',
        () => this.doDetach()
      );
      return;
    }

    // Attach / replace
    const newName =
      this.dozents.find((d) => d.id === this.pendingDozentId)?.name ?? '';
    const isReplace = !!this.originalDozentId;
    const messageKey = isReplace
      ? 'tutor.dozentLink.confirmReplaceMessage'
      : 'tutor.dozentLink.confirmAttachMessage';
    const message = this.translate.instant(messageKey, { name: newName });

    this.openConfirmRaw(
      this.translate.instant(
        isReplace
          ? 'tutor.dozentLink.confirmReplaceTitle'
          : 'tutor.dozentLink.confirmAttachTitle'
      ),
      message,
      () => this.doAttach(this.pendingDozentId)
    );
  }

  /** "Entfernen"-Button on the display card — go straight to the detach
   *  confirm without flipping into edit mode first. */
  confirmDetach() {
    this.openConfirm(
      'tutor.dozentLink.confirmDetachTitle',
      'tutor.dozentLink.confirmDetachMessage',
      () => this.doDetach()
    );
  }

  private openConfirm(titleKey: string, messageKey: string, onYes: () => void) {
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: { title: titleKey, message: messageKey },
      })
      .onClose.subscribe((confirmed) => {
        if (confirmed) onYes();
      });
  }

  /** Same as openConfirm but lets us pass already-translated strings
   *  (the dialog calls `translate` on them, so we bypass that with raw
   *  strings — translate just returns the input unchanged when the key
   *  isn't found). */
  private openConfirmRaw(title: string, message: string, onYes: () => void) {
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: { title, message },
      })
      .onClose.subscribe((confirmed) => {
        if (confirmed) onYes();
      });
  }

  private doAttach(dozentId: string) {
    this.loading = true;
    this.tutorService.attachDozent(this.tutorId, dozentId).subscribe({
      next: () => {
        this.originalDozentId = dozentId;
        this.pendingDozentId = dozentId;
        this.editMode = false;
        this.loading = false;
        this.toastr.success(
          this.translate.instant('tutor.dozentLink.attached'),
          ''
        );
        this.linkChanged.emit(dozentId);
      },
      error: () => {
        this.pendingDozentId = this.originalDozentId;
        this.loading = false;
        this.toastr.danger(
          this.translate.instant('tutor.dozentLink.attachFailed'),
          ''
        );
      },
    });
  }

  private doDetach() {
    this.loading = true;
    this.tutorService.detachDozent(this.tutorId).subscribe({
      next: () => {
        this.originalDozentId = '';
        this.pendingDozentId = '';
        this.editMode = true;
        this.loading = false;
        this.toastr.success(
          this.translate.instant('tutor.dozentLink.detached'),
          ''
        );
        this.linkChanged.emit(null);
      },
      error: () => (this.loading = false),
    });
  }

  /** Twin creation needs the org-level DOZENT feature — the backend
   *  (POST /dozent-bots) enforces the same gate with a 403. Linking an
   *  EXISTING twin stays possible; only creating new ones is gated. */
  get canCreateDozent(): boolean {
    return this.organisationService.hasFeature('DOZENT');
  }

  toggleCreateDialog() {
    this.createDialogVisible = !this.createDialogVisible;
    if (this.createDialogVisible) {
      this.createForm.reset({ name: '', description: '' });
    }
  }

  submitCreate() {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.loading = true;
    const payload = {
      name: this.createForm.value.name,
      description: this.createForm.value.description || undefined,
    };
    this.tutorService.createDozentBot(payload).subscribe({
      next: (created) => {
        this.dozents = [...this.dozents, created];
        this.loading = false;
        this.createDialogVisible = false;
        this.toastr.success(
          this.translate.instant('tutor.dozentLink.created'),
          created.name
        );

        // Stage the newly created dozent in the picker. If nothing was
        // linked before, auto-attach (clear intent — user just created
        // it). If a different dozent IS already linked, route through
        // the same confirm dialog as a regular replace so the existing
        // attachment isn't silently overwritten.
        this.pendingDozentId = created.id;
        if (!this.originalDozentId) {
          this.doAttach(created.id);
        } else {
          this.confirmApplyChange();
        }
      },
      error: () => {
        this.loading = false;
        this.toastr.danger(
          this.translate.instant('tutor.dozentLink.createFailed'),
          ''
        );
      },
    });
  }
}
